import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router';

import { Sheen, WaveText } from '@/components/atoms';
import {
  RIBBON_DURATION_MS,
  RIBBON_EASING,
  RIBBON_ENTRANCE,
  ribbonEntranceKeyframes,
  RIBBON_SETTLED_MS,
  RIBBON_STEP_MS,
} from '@/components/atoms/ribbonMotion';
import { importPromise } from '@/content/importPromise';

const { banner, cta } = importPromise;

/**
 * One entry of an `sx` array. MUI's array form takes style objects, not
 * further `SxProps` — which may themselves be arrays — so a helper feeding
 * one has to say it returns a single object. The same idiom `theme/glass.ts`
 * uses for the same reason.
 */
type PartSx = Exclude<SxProps<Theme>, readonly unknown[]>;

/**
 * One part of the line, and when it arrives.
 *
 * `both` fill is what lets the parts be staggered without any of them being
 * hidden by a standing rule: before its delay each part holds the keyframe's
 * `from`, and after the run it holds `to`. Remove the animation — which is
 * exactly what the reduced-motion rule below does — and the part renders in
 * its natural state with nothing to undo.
 */
const part = (index: number): PartSx => ({
  animation: `${RIBBON_ENTRANCE} ${RIBBON_DURATION_MS}ms ${RIBBON_EASING} ${index * RIBBON_STEP_MS}ms 1 both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/**
 * The ribbon's one clickable thing: the promise, its proof, and the arrow
 * that says it goes somewhere.
 *
 * The whole message is the link rather than a button at the end of a
 * sentence, because a 40px bar has no room for both and a bar that is
 * entirely a target is easier to hit on a phone than a six-word button inside
 * one. It stops short of the full width so the dismiss control beside it is
 * not swallowed.
 *
 * Motion runs in three stages, and only the first repeats. The parts arrive
 * one after another on load; the sheen is held back until they have settled,
 * so the light crosses a finished line rather than one still moving; and from
 * then on the headline ripples every six seconds, which is the only part a
 * visitor who did not happen to be watching the page load will ever see.
 * `atoms/ribbonMotion.ts` has the costs and the reason none of this is a
 * message rotator.
 *
 * Which parts are shown is CSS, not JavaScript: the site is prerendered, so a
 * `matchMedia` decision here would bake one width's wording into the HTML for
 * every visitor. Both clauses are always in the markup — which also means a
 * screen reader on a phone still gets the proof.
 */
export function ImportPromiseRibbonMessage({ playSheen }: { playSheen: boolean }) {
  return (
    <Sheen play={playSheen} delayMs={RIBBON_SETTLED_MS} sx={{ minWidth: 0, borderRadius: 1 }}>
      <Box
        component={RouterLink}
        to={cta.to}
        prefetch="intent"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: { xs: 2, sm: 3 },
          minWidth: 0,
          px: { xs: 2, sm: 3 },
          py: 1,
          borderRadius: 1,
          color: 'inherit',
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: { xs: '0.8125rem', md: '0.875rem' },
          lineHeight: 1.2,
          // The bar is one line at every width; a narrow phone must not be
          // allowed to wrap it into two and push the nav down.
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          [`@keyframes ${RIBBON_ENTRANCE}`]: ribbonEntranceKeyframes,
          '&:hover, &:focus-visible': { textDecoration: 'underline' },
          '& .kbx-ribbon-arrow': {
            flexShrink: 0,
            fontSize: 16,
            transition: (t) =>
              t.transitions.create('transform', { duration: t.transitions.duration.shorter }),
          },
          '&:hover .kbx-ribbon-arrow, &:focus-visible .kbx-ribbon-arrow': {
            transform: 'translate3d(3px, 0, 0)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& .kbx-ribbon-arrow': { transition: 'none' },
            '&:hover .kbx-ribbon-arrow, &:focus-visible .kbx-ribbon-arrow': { transform: 'none' },
          },
        }}
      >
        {/*
          The headline waves; everything after it stays still. One moving
          thing on a 40px bar is a ripple, three is a fairground — and this is
          the clause that has to be read.
        */}
        <Box component="span" sx={part(0)}>
          <WaveText text={banner.promise} />
        </Box>
        <Box component="span" sx={[part(1), { display: { xs: 'none', md: 'inline-block' } }]}>
          {banner.proof}
        </Box>
        {/* The call to action, where there is room for it to be its own words. */}
        <Box
          component="span"
          sx={[
            part(2),
            { display: { xs: 'none', lg: 'inline-block' }, textDecoration: 'underline' },
          ]}
        >
          {banner.action}
        </Box>
        <Box component="span" sx={[part(3), { display: 'inline-flex', flexShrink: 0 }]}>
          <ArrowForwardRounded className="kbx-ribbon-arrow" aria-hidden />
        </Box>
      </Box>
    </Sheen>
  );
}
